import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import Image from '@tiptap/extension-image';
import DropCursor from '@tiptap/extension-dropcursor';
import type { JSONContent } from '@tiptap/core';
import { useEffect } from 'react';
import Toolbar from './Toolbar';

interface PageEditorProps {
  initialContent?: JSONContent | null;
  onChange?: (content: { json: JSONContent; html: string }) => void;
}

export default function PageEditor({ initialContent, onChange }: PageEditorProps) {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Link.configure({ openOnClick: false }),
      Placeholder.configure({ placeholder: 'Start writing your page…' }),
      Image.configure({ inline: false, allowBase64: true }),
      DropCursor,
    ],
    content: initialContent ?? undefined,
    onUpdate({ editor }) {
      onChange?.({ json: editor.getJSON(), html: editor.getHTML() });
    },
  });

  // Apply initialContent changes after mount (e.g. localStorage draft restored async)
  useEffect(() => {
    if (editor && initialContent) {
      editor.commands.setContent(initialContent, { emitUpdate: false });
    }
  }, [editor, initialContent]);

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    const files = Array.from(event.dataTransfer.files).filter(f =>
      f.type.startsWith('image/')
    );
    if (files.length === 0) return;

    event.preventDefault();

    const view = editor?.view;
    if (!view) return;
    const coords = { left: event.clientX, top: event.clientY };
    const pos = view.posAtCoords(coords);
    if (!pos) return;

    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = () => {
        const src = reader.result as string;
        editor
          ?.chain()
          .insertContentAt(pos.pos, { type: 'image', attrs: { src, alt: '' } })
          .run();
      };
      reader.readAsDataURL(file);
    });
  };

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement;
    if (target.tagName !== 'IMG') return;

    const current = (target as HTMLImageElement).alt;
    const next = window.prompt('Image alt text', current);
    if (next === null) return;

    const view = editor?.view;
    if (!view) return;
    const pos = view.posAtDOM(target, 0);
    const node = view.state.doc.nodeAt(pos);
    if (!node) return;
    const { tr } = view.state;
    tr.setNodeMarkup(pos, undefined, { ...node.attrs, alt: next });
    view.dispatch(tr);
  };

  return (
    <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
      <Toolbar editor={editor} />
      <div
        onDrop={handleDrop}
        onDragOver={e => e.preventDefault()}
        onClick={handleClick}
        data-testid="editor-drop-zone"
      >
        <EditorContent
          editor={editor}
          className="prose max-w-none p-4 min-h-[300px]"
        />
      </div>
    </div>
  );
}
